import { api, toApiError } from "@/core/api";
import {
  ActivationResponse,
  ChallengeQuestion,
  EmailCheckResponse,
  ForgotPasswordResponse,
  LoginResponse,
  ResetPasswordResponse,
  SignUpRequest,
  SignUpResponse,
  UpdateProfileResponse,
  UserInfo,
} from "@/core/types";
import { normalizeUserInfo } from "@/core/types/privacy";

export const authService = {
  /**
   * Get current user information
   * @returns Promise with user info or null if not logged in
   */
  async getCurrentUser(): Promise<UserInfo | null> {
    try {
      // Normalise privacy tiers off the wire (short or fully-qualified case names -> bare
      // names) so store consumers work with the UI representation.
      return normalizeUserInfo(await api.get<UserInfo>("/api/userInfo"));
    } catch (error) {
      const apiError = toApiError(error);
      if (apiError.status === 401) {
        // The endpoint uses 401 to report an anonymous session.
        return null;
      }
      throw apiError;
    }
  },

  /**
   * Check if email exists and is activated
   * @param email User email
   * @param autoSendOtp Whether to automatically send OTP if email exists
   * @returns Promise with email check response
   */
  async checkEmail(email: string, autoSendOtp = false): Promise<EmailCheckResponse> {
    return api.post<EmailCheckResponse>("/api/auth/checkEmail", { email, autoSendOtp });
  },

  /**
   * Send OTP code to the user's email
   * @param email User email
   * @returns Promise with OTP sending status
   */
  async sendOtp(email: string): Promise<{ success: boolean; message: string }> {
    return api.post<{ success: boolean; message: string }>("/api/auth/sendOtp", { email });
  },

  /**
   * Login with OTP code (handles 401 error gracefully)
   * @param email User email
   * @param otpCode OTP code
   * @returns Promise with login success status and user data
   */
  async otpLogin(email: string, otpCode: string): Promise<LoginResponse> {
    try {
      return await api.post<LoginResponse>(
        "/api/auth/otpLogin",
        { email, otpCode },
        { fallbackMessage: "Invalid OTP or unauthorized." },
      );
    } catch (error) {
      const apiError = toApiError(error);
      if (apiError.status === 401) {
        // See `signUp` below: the code has to cross the throw-to-data boundary here.
        return {
          success: false,
          message: apiError.message,
          needsAdditionalInfo: false,
          email,
          errorCode: apiError.code,
        };
      }
      throw apiError;
    }
  },

  /**
   * Update user profile after OTP login for new users
   * @param userId User ID
   * @param firstName First name
   * @param lastName Last name
   * @param password Optional password
   * @returns Promise with update result
   */
  async updateUserProfile(
    userId: string,
    firstName: string,
    lastName: string,
    password?: string,
  ): Promise<UpdateProfileResponse> {
    return api.post<UpdateProfileResponse>("/api/auth/updateProfile", {
      userId,
      firstName,
      lastName,
      password,
    });
  },

  /**
   * Login user with password
   * @param email User email
   * @param password User password
   * @returns Promise with login success status
   */
  async login(email: string, password: string): Promise<boolean> {
    // A 2xx resolves; any failure throws an ApiError the caller inspects
    // (e.g. 401 "Account not activated").
    await api.post("/api/auth/login", { email, password });
    return true;
  },

  /**
   * Logout current user
   * @returns Promise with logout success status
   */
  async logout(): Promise<boolean> {
    try {
      await api.post("/api/auth/logout");
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Get a challenge question for signup
   * @returns Promise with challenge question
   */
  async getChallengeQuestion(): Promise<ChallengeQuestion> {
    return api.get<ChallengeQuestion>("/api/auth/challenge");
  },

  /**
   * Register a new user
   * @param signupData User registration data
   * @returns Promise with signup response
   */
  async signUp(signupData: SignUpRequest): Promise<SignUpResponse> {
    try {
      return await api.post<SignUpResponse>("/api/auth/signup", signupData, {
        fallbackMessage: "An error occurred during signup",
      });
    } catch (error) {
      // Carry the code across the throw-to-data boundary, or the caller is left
      // with prose again (#3174): this catch is where a 400's `ApiError` — code
      // and all — would otherwise be flattened to a bare message.
      const apiError = toApiError(error);
      return {
        success: false,
        message: apiError.message,
        errorCode: apiError.code,
      };
    }
  },

  /**
   * Activate user account
   * @param token Activation token
   * @returns Promise with activation response
   */
  async activateAccount(token: string): Promise<ActivationResponse> {
    try {
      return await api.post<ActivationResponse>(
        "/api/auth/activate",
        { token },
        { fallbackMessage: "An error occurred during account activation" },
      );
    } catch (error) {
      // See `signUp` above: the code has to cross the throw-to-data boundary here.
      const apiError = toApiError(error);
      return {
        success: false,
        message: apiError.message,
        errorCode: apiError.code,
      };
    }
  },

  /**
   * Request password reset email
   * @param email User email
   * @returns Promise with forgot password response
   */
  async forgotPassword(email: string): Promise<ForgotPasswordResponse> {
    try {
      return await api.post<ForgotPasswordResponse>(
        "/api/auth/forgotPassword",
        { email },
        { fallbackMessage: "An error occurred processing your request" },
      );
    } catch (error) {
      // See `signUp` above: the code has to cross the throw-to-data boundary here.
      const apiError = toApiError(error);
      return {
        success: false,
        message: apiError.message,
        errorCode: apiError.code,
      };
    }
  },

  /**
   * Reset password with token
   * @param token Reset password token
   * @param password New password
   * @returns Promise with reset password response
   */
  async resetPassword(token: string, password: string): Promise<ResetPasswordResponse> {
    try {
      return await api.post<ResetPasswordResponse>(
        "/api/auth/resetPassword",
        { token, password },
        { fallbackMessage: "An error occurred resetting your password" },
      );
    } catch (error) {
      // See `signUp` above: the code has to cross the throw-to-data boundary here
      // or the caller only ever sees prose (#3174).
      const apiError = toApiError(error);
      return {
        success: false,
        message: apiError.message,
        errorCode: apiError.code,
      };
    }
  },
};
